/**
 * Ryan O'Dowd
 * 2024-02-20
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import Button from '@components/common/Button';
import CustomGoalInput from './CustomGoalInput';
import DailyRings from '@components/DailyRings';
import Dialog from '@components/common/Dialog';
import Icon from '@react-native-vector-icons/material-design-icons';
import {
  RadioButton,
} from 'react-native-paper';
import StreakRepairDisplay from '@components/StreakRepairDisplay';
import Text from '@components/common/Text';
import WidgetBase from '@components/WidgetBase';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';
import {
  updateGoalSettings,
} from '@utils/store/actions';

const DailyGoals = (props: DailyGoalsProps) => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const [_isDialogOpen, setIsDialogOpen] = useState(false);
  const [_isStreakRepairOpen, setIsStreakRepairOpen] = useState(false);

  const goalSettings = hooks.useTypedSelector((state) => state.goalSettings);

  const streaks = hooks.useTypedSelector((state) => state.streaks);
  const oldestRepairableDay = streaks.data?.find((streak) => streak.repairable);

  // @TODO: make this double wide and show checklist next to rings...the items in the list are configurable just like daily goals and are for passage of the day, wrinkle refresh, and something else?
  return (
    <WidgetBase.Container
      layout={props.layout}
      cellSize={props.cellSize}
    >
      <WidgetBase.Header
        title={dateFnsFormat(new Date(), 'MMMM d')}
        rightIconName='cog'
        onPressRightIcon={() => setIsDialogOpen(true)}
      />
      <WidgetBase.Content>
        <View style={styles.ringsWrapper}>{/* @TODO: rename/abstract */}
          <DailyRings
            size={props.cellSize / 1.7 / styles.globalScale}// @TODO: 1.7 looks good but is maybe too hardcoded?
          />
          {!!oldestRepairableDay && (
            <View style={styles.streakWarningWrapper}>
              <TouchableOpacity
                onPress={() => setIsStreakRepairOpen(true)}
              >
                <Icon
                  style={styles.streakWarningIcon}
                  name='calendar-remove'
                />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </WidgetBase.Content>
      <Dialog
        visible={_isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
      >
        <Dialog.Title>{i18n('dailyGoals')}</Dialog.Title>
        <Dialog.Content>
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>{i18n('dailyPeanutsGoal')}</Text>
            <View style={styles.goalSettingsWrapper}>
              {[20, 40, 80, 200].map((goalNum) => {
                return (
                  <View key={goalNum} style={styles.goalSettingsRow}>
                    <RadioButton.Android
                      value={String(goalNum)}
                      status={goalSettings.dailyPeanuts === goalNum ? 'checked' : 'unchecked' }
                      onPress={() => {
                        dispatch(updateGoalSettings({dailyPeanuts: goalNum}));
                      }}
                    />
                    <Text style={styles.labelText}>{goalNum}</Text>
                  </View>
                );
              })}
              <View style={styles.goalSettingsRow}>
                <CustomGoalInput dailyGoalKey='dailyPeanuts' />
                <Text style={styles.labelText}>{i18n('custom')}</Text>
              </View>
            </View>
          </View>
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>{i18n('dailyLessonsGoal')}</Text>
            <View style={styles.goalSettingsWrapper}>
              {[1, 2, 5, 10].map((goalNum) => {
                return (
                  <View key={goalNum} style={styles.goalSettingsRow}>
                    <RadioButton.Android
                      value={String(goalNum)}
                      status={goalSettings.dailyLessons === goalNum ? 'checked' : 'unchecked' }
                      onPress={() => {
                        dispatch(updateGoalSettings({dailyLessons: goalNum}));
                      }}
                    />
                    <Text style={styles.labelText}>{goalNum}</Text>
                  </View>
                );
              })}
              <View style={styles.goalSettingsRow}>
                <CustomGoalInput dailyGoalKey='dailyLessons' />
                <Text style={styles.labelText}>{i18n('custom')}</Text>
              </View>
            </View>
          </View>
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>{i18n('dailyVersesGoal')}</Text>
            <View style={styles.goalSettingsWrapper}>
              {[1, 2, 5, 10].map((goalNum) => {
                return (
                  <View key={goalNum} style={styles.goalSettingsRow}>
                    <RadioButton.Android
                      value={String(goalNum)}
                      status={goalSettings.dailyVersesLeveledUp === goalNum ? 'checked' : 'unchecked' }
                      onPress={() => {
                        dispatch(updateGoalSettings({dailyVersesLeveledUp: goalNum}));
                      }}
                    />
                    <Text style={styles.labelText}>{goalNum}</Text>
                  </View>
                );
              })}
              <View style={styles.goalSettingsRow}>
                <CustomGoalInput dailyGoalKey='dailyVersesLeveledUp' />
                <Text style={styles.labelText}>{i18n('custom')}</Text>
              </View>
            </View>
          </View>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='contained'
            onPress={() => setIsDialogOpen(false)}
          >
            {i18n('close')}
          </Button>
        </Dialog.Actions>
      </Dialog>
      <Dialog
        visible={_isStreakRepairOpen}
        onDismiss={() => setIsStreakRepairOpen(false)}
      >
        <Dialog.Title>{i18n('streakRepair')}</Dialog.Title>
        <Dialog.Content>
          <StreakRepairDisplay
            hideTitle={true}
            repairStreakDate={props.repairStreakDate}
            hasBackground={true}
            // @TODO: add prop for showing dates above the squares
          />
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='contained'
            onPress={() => setIsStreakRepairOpen(false)}
          >
            {i18n('close')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </WidgetBase.Container>
  );
};

class DailyGoalsProps {
  layout: {
    x: number,
    y: number,
    w: number,
    h: number,
  }; // @TODO: type
  cellSize: number;

  repairStreakDate?: string; // @TODO: date object?
};

export default DailyGoals;
